import type { ReactNode } from 'react'

import { cn } from '@/ui/lib/utils'

export interface QuantRailProps {
  side: 'left' | 'right'
  /** Header and vertical strip label, e.g. `Indicators`. */
  label: string
  /** Lower-case noun for the fixed aria-labels: `indicators` -> `Collapse indicators rail`. */
  noun: string
  /** Pinned width in px (250 Indicators, 340 Analyst). */
  width: number
  open: boolean
  /** A collapsed rail overlaid by hover. Never moves a sibling. */
  peeking: boolean
  /** Shown at the bottom of the 34px strip: `3` or `PLANNED`. */
  stripCount: string
  /** Header meta text before the pin button (`3 on`) or a tag node (`Planned`). */
  headerMeta?: ReactNode
  /** Shortcut hint used in the button tooltips. */
  shortcut: string
  onToggle: () => void
  onPointerEnter: (event: { pointerType?: string }) => void
  onPointerLeave: () => void
  children: ReactNode
}

/**
 * Manual trade's collapsible rail (spec 036, handoff 4.1). The slot is a fixed
 * 34px when collapsed and the pinned width when open; a peek overlays the body
 * absolutely inside the slot, so the chart column never reflows. Props only;
 * the open/peek state machine is `useQuantRail`.
 */
export function QuantRail({
  side,
  label,
  noun,
  width,
  open,
  peeking,
  stripCount,
  headerMeta,
  shortcut,
  onToggle,
  onPointerEnter,
  onPointerLeave,
  children,
}: QuantRailProps) {
  const left = side === 'left'
  const showBody = open || peeking
  // The pin glyph points where the rail goes when pressed.
  const glyph = open ? (left ? '‹' : '›') : left ? '›' : '‹'
  const pinLabel = open ? `Collapse ${noun} rail` : `Pin ${noun} rail open`
  const pinTip = open ? `Collapse ( ${shortcut} )` : `Pin open ( ${shortcut} )`
  const edge = left ? 'border-r' : 'border-l'
  return (
    <div
      data-quant-rail={noun}
      data-rail-state={open ? 'open' : peeking ? 'peek' : 'closed'}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      style={{ width: open ? width : 34 }}
      className="relative z-40 shrink-0 transition-[width] duration-[160ms] ease-in-out"
    >
      {!showBody && (
        <div className={cn('absolute inset-0 flex flex-col items-center gap-3.5 bg-paper px-0 pt-2.5 pb-4 text-ink', edge, 'border-ink-20')}>
          <button
            type="button"
            aria-label={`Expand ${noun} rail`}
            title={`Expand ( ${shortcut} )`}
            onClick={onToggle}
            className="flex h-[26px] w-[26px] shrink-0 cursor-pointer items-center justify-center border border-ink-25 bg-transparent text-[13px] leading-none text-ink hover:bg-accent-wash"
          >
            <span aria-hidden="true">{left ? '›' : '‹'}</span>
          </button>
          <span className="flex-1 font-sans text-[10px] font-bold tracking-[0.22em] uppercase [writing-mode:vertical-rl]">
            {label}
          </span>
          <span
            className={cn(
              'shrink-0 font-mono text-ink-50',
              stripCount === 'PLANNED' ? 'text-[8.5px] tracking-[0.1em] uppercase [writing-mode:vertical-rl]' : 'text-[10px]',
            )}
          >
            {stripCount}
          </span>
        </div>
      )}
      {showBody && (
        <aside
          aria-label={`${label} rail`}
          style={{ width }}
          className={cn(
            'absolute top-0 bottom-0 flex flex-col bg-paper text-ink',
            left ? 'left-0' : 'right-0',
            edge,
            'border-ink-20',
            !open && peeking && (left ? 'shadow-[8px_0_24px_rgba(0,0,0,.35)]' : 'shadow-[-8px_0_24px_rgba(0,0,0,.35)]'),
          )}
        >
          <div className="flex h-[46px] shrink-0 items-center gap-2 border-b border-ink-20 pr-2.5 pl-4">
            <span className="flex-1 font-sans text-[12px] font-bold tracking-[0.2em] uppercase">{label}</span>
            {headerMeta}
            <button
              type="button"
              aria-label={pinLabel}
              title={pinTip}
              onClick={onToggle}
              className="flex h-[26px] w-[26px] shrink-0 cursor-pointer items-center justify-center border border-ink-25 bg-transparent text-[13px] leading-none text-ink hover:bg-accent-wash"
            >
              <span aria-hidden="true">{glyph}</span>
            </button>
          </div>
          <div className="flex min-h-0 flex-1 flex-col">{children}</div>
        </aside>
      )}
    </div>
  )
}
